import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Plus, Edit } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { DietTypeSelector } from './DietTypeSelector';
import { EnhancedDietTypeSelector } from './EnhancedDietTypeSelector';
import { MenuItemImageUpload } from './MenuItemImageUpload';
import { menuItemSchema, type MenuItemFormData } from '@/lib/validations/menu';
import { cn } from '@/lib/utils';
import type { MenuItem, MenuSection, DietType, EnhancedDietTypeFormData } from '@/types';

interface MenuItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MenuItemFormData & { imageFile?: File }) => Promise<void>;
  sections: MenuSection[];
  item?: MenuItem | null;
  defaultSectionId?: string;
}

export const MenuItemModal: React.FC<MenuItemModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  sections,
  item,
  defaultSectionId,
}) => {
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [enhancedDietData, setEnhancedDietData] = useState<Partial<EnhancedDietTypeFormData>>({});
  const isEditMode = !!item;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<MenuItemFormData>({
    resolver: zodResolver(menuItemSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      price: '',
      dietType: undefined,
      sectionId: defaultSectionId || '',
      ingredients: '',
      allergens: '',
      preparationTime: '',
      calories: '',
      spiceLevel: undefined,
      isVegan: false,
      isJain: false,
    }
  });

  const watchedValues = watch();

  const hasValidFormData = 
    watchedValues.name &&
    watchedValues.name.trim() !== '' &&
    watchedValues.price &&
    watchedValues.price.trim() !== '' &&
    watchedValues.dietType &&
    watchedValues.sectionId &&
    !errors.name &&
    !errors.price &&
    !errors.dietType &&
    !errors.sectionId &&
    !errors.description &&
    !errors.ingredients &&
    !errors.allergens &&
    !errors.preparationTime &&
    !errors.calories &&
    !errors.root &&
    !submissionError;

  useEffect(() => {
    if (isOpen) {
      if (item) {
        reset({
          name: item.name,
          description: item.description || '',
          price: item.price.toString(),
          dietType: item.dietType,
          sectionId: item.sectionId,
          ingredients: item.ingredients?.join(', ') || '',
          allergens: item.allergens?.join(', ') || '',
          preparationTime: item.preparationTime?.toString() || '',
          calories: item.calories?.toString() || '',
          spiceLevel: item.spiceLevel,
          isVegan: item.isVegan || false,
          isJain: item.isJain || false,
        });
        
        setEnhancedDietData({
          dietType: item.dietType,
          allergens: item.enhancedAllergens || {
            nuts: false,
            dairy: false,
            gluten: false,
            soy: false,
            eggs: false,
            shellfish: false,
            sesame: false,
            mustard: false,
            custom: [],
            mayContain: [],
          },
          spiceLevel: item.enhancedSpiceLevel || undefined,
          cuisineTags: item.cuisineTags || [],
          customTags: item.customTags || [],
          isSignatureDish: item.isSignatureDish || false,
          isChefSpecial: item.isChefSpecial || false,
        });
      } else {
        reset({
          name: '',
          description: '',
          price: '',
          dietType: undefined,
          sectionId: defaultSectionId || '',
          ingredients: '',
          allergens: '',
          preparationTime: '',
          calories: '',
          spiceLevel: undefined,
          isVegan: false,
          isJain: false,
        });
        
        setEnhancedDietData({
          allergens: {
            nuts: false,
            dairy: false,
            gluten: false,
            soy: false,
            eggs: false,
            shellfish: false,
            sesame: false,
            mustard: false,
            custom: [],
            mayContain: [],
          },
          spiceLevel: undefined, // No default spice level
          cuisineTags: [],
          customTags: [],
          isSignatureDish: false,
          isChefSpecial: false,
        });
      }
      setSubmissionError(undefined);
    }
  }, [isOpen, item, defaultSectionId, reset]);

  const handleFormSubmit = async (data: MenuItemFormData) => {
    try {
      setSubmissionError(undefined);
      setImageUploadError(null);
      
      const submissionData = {
        ...data,
        // Always include enhanced diet type fields
        enhancedAllergens: enhancedDietData.allergens,
        enhancedSpiceLevel: enhancedDietData.spiceLevel,
        cuisineTags: enhancedDietData.cuisineTags || [],
        customTags: enhancedDietData.customTags || [],
        isSignatureDish: enhancedDietData.isSignatureDish || false,
        isChefSpecial: enhancedDietData.isChefSpecial || false,
        imageFile: selectedImageFile || undefined,
      };
      
      // Include enhanced diet type data and image file in submission data
      await onSubmit(submissionData);
      
      handleClose();
    } catch (error) {
      console.error('Error submitting item:', error);
      setSubmissionError('Failed to save item. Please try again.');
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setSubmissionError(undefined);
    setSelectedImageFile(null);
    setImageUploadError(null);
    onClose();
  };

  const handleFieldFocus = (fieldName?: keyof MenuItemFormData) => {
    if (fieldName && errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (errors.root?.message) {
      clearErrors('root');
    }
    if (submissionError) {
      setSubmissionError(undefined);
    }
    if (imageUploadError) {
      setImageUploadError(null);
    }
  };

  const handleDietTypeChange = (dietType: DietType) => {
    setValue('dietType', dietType, { shouldValidate: true, shouldDirty: true });
    handleFieldFocus('dietType');
  };

  const handleVeganChange = (isVegan: boolean) => {
    setValue('isVegan', isVegan, { shouldValidate: true, shouldDirty: true });
  };

  const handleJainChange = (isJain: boolean) => {
    setValue('isJain', isJain, { shouldValidate: true, shouldDirty: true });
  };

  const handleImageSelect = (file: File | null) => {
    setSelectedImageFile(file);
    setImageUploadError(null);
  };

  const handleImageUploadError = (error: string) => {
    setImageUploadError(error);
    setSelectedImageFile(null);
  };

  const handlePriceChange = (price: string) => {
    setValue('price', price, { shouldValidate: true, shouldDirty: true });
    handleFieldFocus('price');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
            >
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                {isEditMode ? (
                  <Edit className="w-5 h-5 text-white" />
                ) : (
                  <Plus className="w-5 h-5 text-white" />
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditMode ? 'Edit Menu Item' : 'Add Menu Item'}
              </h2>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Form Content */}
          <form 
            onSubmit={handleSubmit(handleFormSubmit)} 
            className="flex-1 overflow-y-auto p-6 space-y-6" 
            id="menu-item-form"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
                if (hasValidFormData && !isSubmitting) {
                  handleSubmit(handleFormSubmit)();
                }
              }
            }}
          >
              <AnimatePresence>
                {(submissionError || errors.root?.message || imageUploadError) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-3"
                  >
                    <p className="text-sm text-red-600">
                      {submissionError || errors.root?.message || imageUploadError}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Image Upload Section */}
              <div>
                <MenuItemImageUpload
                  currentImageUrl={item?.imageUrl}
                  onImageSelect={handleImageSelect}
                  onUploadError={handleImageUploadError}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <Input
                    label="Item Name"
                    placeholder="e.g., Butter Chicken, Masala Dosa"
                    required
                    error={errors.name?.message}
                    onFocus={() => handleFieldFocus('name')}
                    {...register('name')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 required">
                    Section
                  </label>
                  <select
                    className={cn(
                      "block w-full h-10 px-3 text-base border border-gray-300 rounded-md",
                      "bg-white text-gray-900 placeholder-gray-400",
                      "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
                      "transition-all duration-200",
                      errors.sectionId && "border-red-300 focus:border-apple-red focus:ring-apple-red"
                    )}
                    onFocus={() => handleFieldFocus('sectionId')}
                    {...register('sectionId')}
                  >
                    <option value="">Select a section</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                  {errors.sectionId && (
                    <p className="text-sm text-red-600 mt-1">{errors.sectionId.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Brief description of the item..."
                    className={cn(
                      "block w-full px-3 py-2.5 text-base border border-gray-300 rounded-md",
                      "bg-white text-gray-900 placeholder-gray-400 resize-none",
                      "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
                      "transition-all duration-200",
                      errors.description && "border-red-300 focus:border-apple-red focus:ring-apple-red"
                    )}
                    onFocus={() => handleFieldFocus('description')}
                    {...register('description')}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-600 mt-1">{errors.description.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 required">
                    Price
                  </label>
                  <CurrencyInput
                    placeholder="99.00"
                    value={watchedValues.price || ''}
                    onChange={handlePriceChange}
                    onBlur={() => {
                      // Trigger validation when field is blurred
                      trigger('price');
                    }}
                    onFocus={() => handleFieldFocus('price')}
                    error={errors.price?.message}
                  />
                  {/* Hidden input to register with react-hook-form */}
                  <input
                    type="hidden"
                    {...register('price')}
                    value={watchedValues.price || ''}
                  />
                </div>

                <div>
                  {/* Basic Diet Type Selection */}
                  <DietTypeSelector
                    value={watchedValues.dietType}
                    onChange={handleDietTypeChange}
                    isVegan={watchedValues.isVegan}
                    isJain={watchedValues.isJain}
                    onVeganChange={handleVeganChange}
                    onJainChange={handleJainChange}
                    error={errors.dietType?.message}
                    onFieldFocus={() => handleFieldFocus('dietType')}
                  />

                  {/* Enhanced Diet Type Options */}
                  <div className="mt-6">
                    <EnhancedDietTypeSelector
                      value={{ 
                        dietType: watchedValues.dietType,
                        allergens: enhancedDietData.allergens,
                        spiceLevel: enhancedDietData.spiceLevel,
                        cuisineTags: enhancedDietData.cuisineTags,
                        customTags: enhancedDietData.customTags,
                        isSignatureDish: enhancedDietData.isSignatureDish,
                        isChefSpecial: enhancedDietData.isChefSpecial || false,
                      }}
                      onChange={(data) => {
                        if (data.dietType) {
                          handleDietTypeChange(data.dietType);
                        }
                        setEnhancedDietData(prevData => ({ ...prevData, ...data }));
                      }}
                      errors={errors.dietType?.message ? {
                        dietType: errors.dietType.message,
                      } : undefined}
                      onFieldFocus={() => handleFieldFocus('dietType')}
                    />
                  </div>
                </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Preparation Time (minutes)"
                    type="number"
                    placeholder="15"
                    min="1"
                    max="180"
                    error={errors.preparationTime?.message}
                    onFocus={() => handleFieldFocus('preparationTime')}
                    {...register('preparationTime')}
                  />

                  <Input
                    label="Calories (kcal)"
                    type="number"
                    placeholder="350"
                    min="1"
                    max="5000"
                    error={errors.calories?.message}
                    onFocus={() => handleFieldFocus('calories')}
                    {...register('calories')}
                  />
                </div>

                <Input
                  label="Ingredients"
                  placeholder="tomato, onion, ginger, garlic"
                  error={errors.ingredients?.message}
                  onFocus={() => handleFieldFocus('ingredients')}
                  {...register('ingredients')}
                />
              </div>
            </div>
          </form>

          {/* Footer - Fixed */}
          {/* Action Buttons */}
          <div className="flex space-x-3 p-6 pt-2 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              form="menu-item-form"
              variant="primary"
              disabled={!hasValidFormData || isSubmitting}
              loading={isSubmitting}
              className="flex-1"
            >
              {isEditMode ? 'Update Item' : 'Add Item'}
            </Button>
          </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
